import {
  FiltersPopover,
  SectionLabel,
} from '@/components/Display/FiltersPopover'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Checkbox, Select } from 'antd'
import {
  EMPTY_LOG_FILTERS,
  EVENT_OPTIONS,
  ISSUE_TYPE_OPTIONS,
  type LogFilters,
  type OwnerOption,
  STATE_OPTIONS,
  activeLogFilterCount,
} from './logsFilterModel'

// Wide enough that no event label wraps next to its severity tag.
const CONTENT_WIDTH = 460
const DOT_SIZE = 10

const NOWRAP_STYLE = { whiteSpace: 'nowrap' as const }

function StateDot({ color }: { color: string }) {
  return (
    <div
      style={{
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  )
}

function toggle<T>(values: T[], id: T): T[] {
  return values.includes(id)
    ? values.filter((value) => value !== id)
    : [...values, id]
}

export function LogsFilterPopover({
  value,
  onChange,
  ownerOptions,
}: {
  value: LogFilters
  onChange: (filters: LogFilters) => void
  /** Tenants to choose from, derived from the rows currently loaded. */
  ownerOptions: OwnerOption[]
}) {
  return (
    <FiltersPopover
      width={CONTENT_WIDTH}
      activeCount={activeLogFilterCount(value)}
      onClear={() => onChange(EMPTY_LOG_FILTERS)}
    >
      <Vertical gap={4}>
        <SectionLabel>Issue Owner</SectionLabel>
        <Select
          mode='multiple'
          placeholder='Any owner'
          allowClear
          maxTagCount='responsive'
          value={value.issueOwners}
          options={ownerOptions}
          onChange={(issueOwners: string[]) =>
            onChange({ ...value, issueOwners: issueOwners ?? [] })
          }
          style={{ width: '100%' }}
        />
      </Vertical>

      <Vertical gap={4}>
        <SectionLabel>Issue / Warning Type</SectionLabel>
        <Select
          mode='multiple'
          placeholder='Any type'
          allowClear
          maxTagCount='responsive'
          value={value.issueTypes}
          options={ISSUE_TYPE_OPTIONS}
          onChange={(issueTypes: string[]) =>
            onChange({ ...value, issueTypes: issueTypes ?? [] })
          }
          style={{ width: '100%' }}
        />
      </Vertical>

      <Horizontal gap={24} alignItems='flex-start'>
        <Vertical gap={8} flex={1}>
          <SectionLabel>State</SectionLabel>
          {STATE_OPTIONS.map((option) => (
            <Checkbox
              key={option.label}
              checked={value.states.includes(option.id)}
              onChange={() =>
                onChange({ ...value, states: toggle(value.states, option.id) })
              }
            >
              <Horizontal gap={8} alignItems='center'>
                <StateDot color={option.color} />
                <Texto>{option.label}</Texto>
              </Horizontal>
            </Checkbox>
          ))}
        </Vertical>

        <Vertical gap={8} flex={1}>
          <SectionLabel>Events / Checkpoints</SectionLabel>
          {EVENT_OPTIONS.map((option) => (
            <Horizontal key={option.id} gap={8} alignItems='center'>
              <Checkbox
                checked={value.events.includes(option.id)}
                onChange={() =>
                  onChange({
                    ...value,
                    events: toggle(value.events, option.id),
                  })
                }
              >
                <Texto style={NOWRAP_STYLE}>{option.label}</Texto>
              </Checkbox>
              <SeverityTag
                severity={option.severity}
                style={{ marginLeft: 'auto' }}
              />
            </Horizontal>
          ))}
        </Vertical>
      </Horizontal>
    </FiltersPopover>
  )
}
